import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../ui/sheet';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Separator } from '../../../ui/separator';
import { Flag, CheckCircle2, MapPin, Calendar, Home, Car } from 'lucide-react';
import { prettyDate } from '../../fixtures';

const Row = ({ label, value, mono }) => (
  <div className='flex items-start justify-between gap-4 py-1.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    <span className={`text-sm text-right ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</span>
  </div>
);

const RespondentDetailSheet = ({ open, onOpenChange, respondent }) => {
  if (!respondent) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='sm:max-w-lg overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>{respondent.name}</SheetTitle>
          <SheetDescription>Per-component breakdown and raw response audit.</SheetDescription>
        </SheetHeader>

        <div className='flex items-center gap-2 mt-4 mb-5'>
          <Badge variant='info' size='default'>
            <MapPin size={10} strokeWidth={1.5} />
            {respondent.location}
          </Badge>
          <Badge variant='success' size='default'>
            <CheckCircle2 size={10} strokeWidth={1.5} />
            {respondent.employmentType}
          </Badge>
          <Badge variant='outline' size='default'>
            <Calendar size={10} strokeWidth={1.5} />
            {respondent.weeksWorked} weeks
          </Badge>
        </div>

        <div className='rounded-lg border border-border p-4 mb-5'>
          <div className='font-display text-sm mb-2'>Working pattern</div>
          <Row label='Days per week' value={`${respondent.workPattern.daysPerWeek}`} />
          <Row label='WFH days' value={`${respondent.workPattern.wfhDays}`} />
          <Row label='Commute days' value={`${respondent.workPattern.commuteDays}`} />
          <Row label='Submitted' value={prettyDate(respondent.submittedAt)} />
        </div>

        <div className='rounded-lg border border-border p-4 mb-5'>
          <div className='flex items-center gap-2 font-display text-sm mb-2'>
            <Home size={14} strokeWidth={1.5} className='text-accent' />
            WFH components
          </div>
          <Row label='Equipment kWh' value={respondent.wfh.equipmentKwh.toFixed(1)} mono />
          <Row label='Video call kWh' value={respondent.wfh.videoKwh.toFixed(1)} mono />
          <Row label='Lighting kWh' value={respondent.wfh.lightingKwh.toFixed(1)} mono />
          <Row label='Electric heating kWh' value={respondent.wfh.electricHeatingKwh.toFixed(1)} mono />
          <Row label='Gas kWh' value={respondent.wfh.gasKwh.toFixed(1)} mono />
          <Row label='Oil kWh' value={respondent.wfh.oilKwh.toFixed(1)} mono />
          <Row label='Solid fuel kWh' value={respondent.wfh.solidFuelKwh.toFixed(1)} mono />
          <Row label='Cooling kWh' value={respondent.wfh.coolingKwh.toFixed(1)} mono />
          <Separator className='my-2' />
          <Row label='WFH total' value={`${respondent.wfh.totalTco2e.toFixed(3)} tCO2e`} mono />
          <div className='mt-2'>
            <Badge variant='outline' size='sm'>
              Electricity source: {respondent.renewableFlag}
            </Badge>
          </div>
        </div>

        <div className='rounded-lg border border-border p-4 mb-5'>
          <div className='flex items-center gap-2 font-display text-sm mb-2'>
            <Car size={14} strokeWidth={1.5} className='text-accent' />
            Commuting legs
          </div>
          {respondent.commuting.legs.map((leg) => (
            <div key={leg.order} className='flex items-center justify-between py-2 border-b border-border last:border-b-0'>
              <div>
                <div className='text-xs text-muted-foreground font-mono'>Leg {leg.order}</div>
                <div className='text-sm font-medium'>{leg.mode}</div>
              </div>
              <div className='text-right'>
                <div className='font-mono text-sm tabular-nums'>{leg.distance} km / one way</div>
                <div className='text-xs text-muted-foreground'>
                  {leg.annualKm.toLocaleString()} km/yr · {leg.tco2e.toFixed(3)} tCO2e
                </div>
              </div>
            </div>
          ))}
          <Separator className='my-2' />
          <Row label='Total annual km' value={respondent.commuting.totalKm.toLocaleString()} mono />
          <Row label='Commute total' value={`${respondent.commuting.totalTco2e.toFixed(3)} tCO2e`} mono />
        </div>

        <div className='flex items-center justify-end gap-2 mt-6'>
          <Button variant='outline' size='sm'>
            <Flag size={14} strokeWidth={1.5} />
            Flag for review
          </Button>
          <Button variant='destructive' size='sm'>
            Exclude from calc
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default RespondentDetailSheet;
